<template>
    <div
        ref="calculator"
        class="mod mod-calculator skin-calculator-baudarlehen partial"
        data-framework="vue"
    >
        <div class="form calculator">
            <h2
                v-if="!showInitError"
                class="title-page"
            >
                {{ heading }}
            </h2>
            <div class="calculator-content">
                <rechner-fin-bestaetigung
                    v-if="!showInitError"
                    :show-hinweis-rate="showHinweisRate"
                />
                <ergebnis-fin-bestaetigung
                    :show-server-error="showInitError || showCalcError"
                    @check-if-finanzierung-possible="onCheckIfFinanzierungPossible"
                />
            </div>
            <berechnungsvergleich
                v-if="!showInitError && showVergleich"
                @initTooltips="onInitTooltips"
            />
        </div>
    </div>
</template>

<script>
import {defineComponent} from 'vue';
import 'bootstrap/dist/js/bootstrap.min';
import EZielwert from "@/enums/EZielwert";
import {baudarlehenBerechnung, baudarlehenInitialisierung, baudarlehenVergleichsmodelle} from "@/services/baudarlehenService";
import EHinweisTypes from "@/enums/EHinweisTypes";
import {initTooltips} from "@/utils/TooltipUtil";
import {trackFinBestaetigungInput, trackGAEvent} from "@/utils/TrackingUtil";
import {isCalcTrigger} from "@/types/CalcTrigger";
import RechnerFinBestaetigung from "@/components/rechner/RechnerFinBestaetigung";
import ErgebnisFinBestaetigung from "@/components/ergebnis/ErgebnisFinBestaetigung";
import Berechnungsvergleich from "@/components/vergleich/Berechnungsvergleich";

export default defineComponent({
    name: 'AppFinBestaetigung',
    components: {Berechnungsvergleich, ErgebnisFinBestaetigung, RechnerFinBestaetigung},
    data() {
        return {
            showInitError: false,
            showCalcError: false,
            showHinweisRate: false,
            showVergleich: true
        }
    },
    computed: {
        initData() {
            return this.$store.state
        },
        hinweisType() {
            return EHinweisTypes.ERROR
        },
        hinweisHeadline() {
            return this.$store.state.fachtexte["rechner.ergebis.fehler.ueberschrift"]
        },
        hinweisMessage() {
            return this.$store.state.fachtexte["rechner.ergebnis.fehlertext"]
        },
        heading() {
            return this.$store.state.fachtexte["rechner.finanzierungsbestaetigung.titel"];
        }
    },
    mounted() {
        initTooltips('#gui_finBestaetigung');

        this.initialisierungDaten();

        this.$store.subscribe((mutation) => {
            if (isCalcTrigger(mutation.type)) {
                this.callBerechnungen();
                trackFinBestaetigungInput(mutation.type, mutation.payload);
            }
        });

        //Timeout, damit es sicher erst nach den DOM Ready vom Google Tagmanager getrackt wird
        document.onreadystatechange = () => {
            if (document.readyState == "complete") {
                setTimeout(trackGAEvent(this.$store.state.rechnerNameGAEvents, "1_dateneingabe_baudarlehen", ""), 100);
            }
        }
    },
    methods: {
        async initialisierungDaten() {
            const rechnerLib = this.getRechnerLib();
            this.$store.state.rechnerNameGAEvents = "calc_fin_bestaetigung";

            try {
                const response = await baudarlehenInitialisierung(rechnerLib);
                this.showInitError = false;
                await this.$store.commit("initDefaultValues", response);
                await this.callBerechnungen();
            } catch (error) {
                this.showInitError = true;
            }
        },

        //Auslesen der konfigurierten Rechnerlib aus dem Webclipping Kontext
        getRechnerLib() {
            let webclippingElement = this.$refs.calculator.parentElement.parentElement.parentElement;
            let rechnerLibObject = JSON.parse(webclippingElement.dataset.webclippingConfig);
            return rechnerLibObject.rechnerLib;
        },

        async callModellberechnung() {
            try {
                let anfrage = this.getAnfrage();
                const response = await baudarlehenBerechnung(anfrage);
                this.showCalcError = false;

                this.$store.commit("handleResult", response);
            } catch (error) {
                this.showCalcError = true;
            }
        },

        async callVergleichsberechnung() {
            try {
                let anfrage = this.getAnfrage();
                const response = await baudarlehenVergleichsmodelle(anfrage);
                this.showCalcError = false;
                this.$store.commit("handleVergleichsmodelle", response);
            } catch (error) {
                this.showCalcError = true;
            }
        },

        getAnfrage() {
            let kaufpreis = this.$store.state.sliderPreis.value;
            let rate = this.$store.state.sliderEinnahmen.value - this.$store.state.sliderAusgaben.value;
            if (rate < 0) {
                rate = 0;
            }

            return {
                darlehensumme: kaufpreis - this.$store.state.sliderEigenkapital.value,
                dauerSollzinsbindung: this.$store.state.selectedSollzinsbindung,
                kaufpreis: kaufpreis,
                mindertilgung: false,
                sondertilgungJaehrlich: null,
                volltilgung: false,
                zielwert: {value: rate, zielwert: EZielwert.ANNUITAET}
            };
        },

        async callBerechnungen() {
            await this.callModellberechnung();
            if (this.showVergleich) {
                await this.callVergleichsberechnung();
            }
        },

        onInitTooltips(selector) {
            this.$nextTick(() => {
                initTooltips(selector);
            });
        },

        onCheckIfFinanzierungPossible(finanzierungStatus) {
            this.showVergleich = Boolean(finanzierungStatus);
        }
    }
});
</script>
